import { moveToFulfilled, moveToPending, moveToRejected, remove } from '@ms/yammer-data-support';
import type { Reducer } from 'redux';

import type {
  TopicsAllLoadFulfilledAction,
  TopicsAllLoadMoreFulfilledAction,
} from '../../domains/engageTopicsAll/actions.types';
import type {
  SuggestedTopicsLoadFulfilledAction,
  SuggestedTopicsLoadMoreFulfilledAction,
} from '../../domains/suggestedTopics/actions.types';
import type { FollowTopicAction, UnfollowTopicAction } from '../../domains/topicFollow/actions.types';
import type { TopicHeaderLoadAction } from '../../domains/topicHeaderLoad/actions.types';
import type { TopicCreateFulfilledAction } from '../../domains/topics/createActions.types';
import type { TopicEditAction } from '../../domains/topics/editActions.types';
import type { RemoveTopicFulfilledProps } from '../../domains/topics/removeTopicActions';
import type { RemoveTopicFulfilledAction } from '../../domains/topics/removeTopicActions.types';
import type {
  TopicsFollowedLoadAction,
  TopicsFollowedLoadMoreAction,
} from '../../domains/topicsFollowedLoad/actions.types';
import type {
  TrendingTopicsLoadFulfilledAction,
  TrendingTopicsLoadMoreFulfilledAction,
} from '../../domains/trendingTopicsLoad/actions.types';
import type { TypeaheadFulfilledAction } from '../../domains/typeahead/actions.types';

import type { TopicPermissions } from './types';

export type TopicPermissionAction =
  | FollowTopicAction
  | TopicHeaderLoadAction
  | UnfollowTopicAction
  | TopicEditAction
  | TrendingTopicsLoadFulfilledAction
  | TrendingTopicsLoadMoreFulfilledAction
  | TopicsAllLoadFulfilledAction
  | TopicsAllLoadMoreFulfilledAction
  | SuggestedTopicsLoadFulfilledAction
  | SuggestedTopicsLoadMoreFulfilledAction
  | TopicCreateFulfilledAction
  | TopicsFollowedLoadAction
  | TopicsFollowedLoadMoreAction
  | TypeaheadFulfilledAction
  | RemoveTopicFulfilledAction;

const initialState: TopicPermissions = {
  pending: { byId: {} },
  fulfilled: { byId: {} },
  rejected: { byId: {} },
};

const removeTopic = (state: TopicPermissions, topicIds: RemoveTopicFulfilledProps) => {
  const curatedTopicId = topicIds?.curatedTopicId;

  return curatedTopicId ? remove(state, [curatedTopicId]) : state;
};

const reducer: Reducer<TopicPermissions, TopicPermissionAction> = (state = initialState, action) => {
  switch (action.type) {
    case 'TOPIC_HEADER.LOAD_PENDING':
      return moveToPending(state, [action.payload.topicId]);
    case 'TOPIC_HEADER.LOAD_REJECTED':
      return moveToRejected(state, [action.payload.topicId], action.payload.error);
    case 'TOPIC_HEADER.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.topicPermissions);
    case 'TRENDING_TOPICS.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.topicPermissions);
    case 'TRENDING_TOPICS.LOAD_MORE_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.topicPermissions);
    case 'TOPICS_FOLLOWED.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.topicPermissions);
    case 'TOPICS_FOLLOWED.LOAD_MORE_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.topicPermissions);
    case 'TOPIC.CREATE_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.topicPermissions);
    case 'TOPICS_ALL.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.topicPermissions);
    case 'TOPICS_ALL.LOAD_MORE_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.topicPermissions);
    case 'SUGGESTED_TOPICS.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.topicPermissions);
    case 'SUGGESTED_TOPICS.LOAD_MORE_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.topicPermissions);
    case 'TYPEAHEAD.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.topicPermissions);
    case 'TOPIC.REMOVE_FULFILLED':
      return removeTopic(state, action.payload);
    default:
      return state;
  }
};

export default reducer;
